import { useCallback, useContext, useMemo } from 'react';
import { IssuerPortfolioAssetFullInfoModal } from '@/modules/issuer-dashboard/components/portfolios/IssuerPortfolioAssetFullInfoModal.tsx';
import { useIssuerPortfolioTreemapChartByIdQuery } from '@/modules/issuer-dashboard/queries/portfolios.tsx';
import {
  ChartEmptyState,
  ChartTreemap,
  InfoCard,
  ModalProviderContext,
  Spinner,
  TreemapItem,
} from 'ui';

export interface ChartTreemapAssetProps {
  portfolioId: number;
}

export const ChartTreemapAsset = ({ portfolioId }: ChartTreemapAssetProps) => {
  const { openModal } = useContext(ModalProviderContext);
  const {
    isFetching,
    isLoading,
    isError,
    data: dataTreemapChart,
  } = useIssuerPortfolioTreemapChartByIdQuery({
    portfolio_id: portfolioId,
  });

  const handleOnClick = useCallback(
    (assetId: number, assetName: string) => {
      openModal(IssuerPortfolioAssetFullInfoModal, {
        id: assetId,
        name: assetName,
        portfolioId,
      });
    },
    [openModal, portfolioId],
  );

  const [isHave, items] = useMemo<[boolean, TreemapItem[]]>(() => {
    if (!dataTreemapChart?.data?.filter(item => item.value > 0)?.length) return [false, []];

    const isHave = (dataTreemapChart?.data?.length || 0) > 0;
    const items: TreemapItem[] = dataTreemapChart?.data?.map<TreemapItem>(item => ({
      name: item.name,
      pnl_percent: +item.pnl_ratio,
      value: +item.value < 0 ? 0 : +item.value,
      originalValue: +item.value,
      currency: item.currency,
      quantity: +item.quantity,
      pnl: +item.pnl,
      id: item.asset_id,
      onClick: handleOnClick,
    }));

    return [isHave, items];
  }, [dataTreemapChart?.data, handleOnClick]);

  return (
    <InfoCard
      title="Assets"
      headerAction={
        <div className="flex items-center justify-end gap-2">
          {isFetching && !isLoading && <Spinner size="sm" />}
        </div>
      }
      classes={{
        content: 'p-0 px-6',
      }}
    >
      {!isLoading && isHave && <ChartTreemap items={items} colorNeutral="#24699F" />}
      {!isHave && (
        <div className="py-4 flex justify-center items-center font-medium text-lg">
          <ChartEmptyState isError={isError} isLoading={isLoading} />
        </div>
      )}
    </InfoCard>
  );
};
